import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Disclaimer } from "@/components/Shared";
import { WizardProgress, FieldGrid, WizardNav, validateStep, PortalHero } from "@/components/pathways/FormBits";
import { Scale, CheckCircle2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const STEPS = [
  { title: "Personal details", fields: [
    { key: "full_name", label: "Full name", required: true },
    { key: "marn", label: "MARN", required: true, placeholder: "e.g. 2619415" },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "phone", label: "Phone", required: true },
    { key: "whatsapp", label: "WhatsApp (if different)" },
    { key: "country", label: "Country", required: true },
    { key: "office_address", label: "Office address" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "website", label: "Website" },
  ]},
  { title: "Professional profile", fields: [
    { key: "years_registered", label: "Years registered as an RMA", type: "number", required: true },
    { key: "practice_type", label: "Current practice", type: "select", required: true,
      options: ["Sole Practitioner", "Migration Firm", "Lawyer", "Consultant", "Other"] },
    { key: "languages", label: "Languages spoken", placeholder: "e.g. English, Hindi, Nepali" },
    { key: "annual_volume", label: "Visa applications handled annually", type: "select",
      options: ["Under 50", "50–100", "100–300", "300+"] },
    { key: "years_experience", label: "Years of migration experience", type: "number" },
  ]},
  { title: "Practice & collaboration", fields: [
    { key: "areas", label: "Areas of practice", type: "multi", required: true,
      options: ["Student Visa", "Visitor Visa", "Skilled Migration", "Employer Sponsorship", "Partner Visa", "Parent Visa", "Citizenship", "Appeals", "Ministerial Intervention", "Compliance", "Business Visas"] },
    { key: "collaboration", label: "Collaboration opportunities that interest you", type: "multi",
      options: ["Receiving client referrals", "Working with our Education Agents", "White Label Services", "Overflow Case Work", "Regional Coverage", "Joint Marketing", "Training", "Speaking Opportunities", "Software Access", "Assessment Platform Access"] },
  ]},
  { title: "Availability", fields: [
    { key: "states", label: "States you service", type: "multi", options: AU_STATES },
    { key: "consult_types", label: "Consultation types you provide", type: "multi",
      options: ["Online consultations", "Face-to-face consultations", "Weekend appointments", "Emergency appointments"] },
  ]},
  { title: "Documents & comments", fields: [
    { key: "document_links", label: "Links to your professional documents", type: "textarea",
      placeholder: "Paste links (Google Drive / Dropbox) to your professional profile, CV, indemnity insurance or company profile. We can also request documents by email after review." },
    { key: "comments", label: "Additional comments", type: "textarea", placeholder: "Anything else you'd like us to know…" },
  ]},
];

export default function RmaNetwork() {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isLast = step === STEPS.length - 1;
  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const next = async () => {
    const missing = validateStep(STEPS[step].fields, values);
    if (missing) { toast.error(`Please complete: ${missing}`); return; }
    if (!isLast) { setStep(step + 1); top(); return; }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/rma-network/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, consent: true, consent_timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setResult(data);
      toast.success("Network application received!");
      top();
    } catch (err) { toast.error(err.message || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="rma-network-result" className="bg-white">
        <PortalHero icon={CheckCircle2} eyebrow="Application received" title="Thank you for your interest in joining our Migration Network." subtitle="Your application has been received and our team will be in touch shortly." />
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Registration ID</div>
              <div className="font-display text-3xl text-[#29324A] mt-1" data-testid="rma-reference">{result.reference_id}</div>
            </div>

            <div className="mt-6 bg-white border border-slate-200 p-6" data-testid="rma-profile-summary">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Your profile summary</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
                <div><div className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">MARN</div><div className="text-slate-800">{values.marn}</div></div>
                <div><div className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">Experience level</div><div className="text-slate-800">{values.years_experience || values.years_registered} years</div></div>
                <div className="sm:col-span-2"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">Primary visa categories</div><div className="text-slate-800">{(values.areas || []).join(", ") || "—"}</div></div>
                <div><div className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">Languages</div><div className="text-slate-800">{values.languages || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">Preferred collaboration</div><div className="text-slate-800">{(values.collaboration || []).join(", ") || "—"}</div></div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-6 md:p-8">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#C43A89]">Recommended opportunities</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {result.recommended_opportunities.map((o) => (
                  <div key={o} className="flex gap-2 text-[13px] text-slate-100"><CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> {o}</div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Link to="/book-consultation" data-testid="rma-meeting-cta" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Schedule Introduction Meeting <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="text-[13px] text-slate-500 mt-4">A confirmation email has been sent to <strong>{values.email}</strong>.</p>
          </div>
        </section>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div data-testid="rma-network-page" className="bg-white">
      <PortalHero
        icon={Scale}
        eyebrow="Migration Network · Australia & New Zealand"
        title="Join our Migration Network."
        subtitle="Partner with a Registered Migration Agent (Australia · MARN 2619415) and Licensed Immigration Adviser (New Zealand · IAA 202603837). Receive quality referrals, collaborate with education agents and access technology designed for migration professionals across Australia and New Zealand."
      />
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 shadow-sm border-l-4 border-l-[#29324A] p-6 md:p-8">
            <WizardProgress titles={STEPS.map((s) => s.title)} current={step} />
            <FieldGrid fields={STEPS[step].fields} values={values} onChange={onChange} />
            {isLast && <div className="mt-6"><ConsentCheckbox value={consent} onChange={setConsent} testid="rma-consent" /></div>}
            <WizardNav onBack={() => { setStep(Math.max(0, step - 1)); top(); }} onNext={next}
              nextLabel={isLast ? "Submit Application" : "Continue"} loading={loading} showBack={step > 0} />
          </div>
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}
